import {
  useState,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useForm } from "react-hook-form";
import "../../styles/PatientDetail.css";
import { UpdatePatientDetails, GetStateList, GetCaseTypeList } from "./Service";
import toast from "react-hot-toast";
import WebcamModal from "../../Component/shared/webCam/Index";
import ImagePreviewModal from "../../Component/shared/imagePreview/Index";
import PatientDetailShimmer from "../../Component/shared/loader/shimmerLoader/patientDetailShimmer/PatientDetailShimmer";
import SelectDropdown from "../../Component/shared/dropdown/selectDropdown/Index";
import DatePicker from "../../Component/shared/datePicker/Index";
import MedicalHistory from "../medicalHistory/MedicalHistory";
import RateReviewModal from "../rateUs/RateReviewModal";
import { GetProcedureCodeList } from "../medicalHistory/Service";
import { setSessionItem } from "../../helpers/Util";
import { CaseTypeList, PatientDetails, StateList } from "../../Constant";
import { usePatientData } from "../../commonFunction/storage/Patientdatacontext";
import { Dropdown } from "react-bootstrap";
import AddAppointmentModal from "./AddAppointmentModal";
import CheckInModal from "./CheckInModal";
import { getRateReviewTemplate } from "../rateUs/rateReviewTemplate";
import { CameraIcon } from "../../Component/shared/icons/Index";

function PFld({ label, children }) {
  return (
    <div className="pd-field">
      <span className="pd-label">{label}</span>
      {children}
    </div>
  );
}
const RequiredStar = () => <span style={{ color: "#e63946" }}>*</span>;

// convert "2026-02-23T00:00:00" → "02/23/2026"
const ConvertDateFormatForUI = (val) => {
  if (val === "1900-01-01T00:00:00" || val === undefined) return null;
  if (!val || typeof val !== "string") return null;
  const iso = val.includes("T") ? val.slice(0, 10) : val;
  const [yyyy, mm, dd] = iso.split("-");
  if (!yyyy || !mm || !dd) return null;
  return `${mm}/${dd}/${yyyy}`;
};

// convert "02/23/2026" → "2026-02-23T00:00:00"
const ConvertDateFormatForAPI = (val) => {
  if (!val || typeof val !== "string") return null;
  if (val.includes("T")) return val;
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(val)) {
    const [mm, dd, yyyy] = val.split("/");
    return `${yyyy}-${mm}-${dd}T00:00:00`;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return `${val}T00:00:00`;
  return null;
};
const toImageSrc = (raw) => {
  if (!raw || typeof raw !== "string" || raw.trim() === "") return null;
  if (raw.startsWith("data:")) return raw;
  if (raw.startsWith("http")) return raw;
  return `data:image/jpeg;base64,${raw}`;
};

const ALLOWED_TYPES = ["image/jpeg", "image/jpg", "image/png"];
const MAX_PHOTO_SIZE = 2 * 1024 * 1024; // 2 MB
const validatePhoto = (file) => {
  if (!ALLOWED_TYPES.includes(file.type)) return "Only JPG and PNG are allowed.";
  if (file.size > MAX_PHOTO_SIZE) return "File size should be less than 2MB.";
  return null;
};

const DATE_FIELDS = ["dateOfFirstTreatment", "patientDOB"];
const PATIENT_FIELDS = [
  "patientFirstName", "patientMiddleName", "patientLastName",
  "patientDrivingLicense", "patientDOB", "patientMaritalStatus",
  "patientGender", "height", "weight", "patientAddress", "patientStreet",
  "patientCity", "patientZIP", "patientCellNumber", "patientStateId",
  "patientEmail", "profileImagePath", "profileImageData",
];
const REVIEW_SETTINGS_FIELDS = [
  "allowReviewRequest",
  "allowTestimonialReuse",
  "communicationPreference",
];

const AppointmentDropdown = ({ className = "", onBookAppointment, onCheckInClick }) => (
  <Dropdown className={className}>
    <Dropdown.Toggle
      id="appt-dropdown"
      className="pd-icon-btn pd-icon-btn--med"
    >
      <svg className="pd-med-btn-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
      </svg>
      <span className="pd-med-btn-text">Appointment</span>
      <div className="pd-icon-btn__tooltip pd-med-btn-tooltip">Appointment</div>
    </Dropdown.Toggle>
    <Dropdown.Menu className="pd-appt-dropdown-menu">
      <Dropdown.Item className="pd-appt-dropdown-item" onClick={onBookAppointment}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 8, flexShrink: 0 }}>
          <path d="M12 5v14M5 12h14" />
        </svg>
        Book an appointment
      </Dropdown.Item>
      <Dropdown.Item className="pd-appt-dropdown-item" onClick={onCheckInClick}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 8, flexShrink: 0 }}>
          <path d="M20 6L9 17l-5-5" />
        </svg>
        Check In
      </Dropdown.Item>
    </Dropdown.Menu>
  </Dropdown>
);

const MedHistoryBtn = ({ onClick }) => (
  <button type="button" onClick={onClick} className="pd-icon-btn pd-icon-btn--med" aria-label="View Medical History">
    <svg className="pd-med-btn-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4.8 2.3A.3.3 0 1 0 5 2H4a2 2 0 0 0-2 2v5a6 6 0 0 0 6 6 6 6 0 0 0 6-6V4a2 2 0 0 0-2-2h-1a.2.2 0 1 0 .3.3" />
      <path d="M8 15v1a6 6 0 0 0 6 6v0a6 6 0 0 0 6-6v-4" />
      <circle cx="20" cy="10" r="2" />
    </svg>
    <span className="pd-med-btn-text">Medical History</span>
    <div className="pd-icon-btn__tooltip pd-med-btn-tooltip">Medical History</div>
  </button>
);

const RateReviewBtn = ({ onClick }) => (
  <button type="button" onClick={onClick} className="pd-icon-btn pd-icon-btn--med" aria-label="Rate & Review">
    <svg className="pd-med-btn-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
    <span className="pd-med-btn-text">Rate &amp; Review</span>
    <div className="pd-icon-btn__tooltip pd-med-btn-tooltip">Rate &amp; Review</div>
  </button>
);

const PatientDetail = forwardRef((props, ref) => {
  const fileRef = useRef(null);
  const avatarRef = useRef(null);

  const [photoData, setPhotoData] = useState(null);
  const [newPhotoBase64, setNewPhotoBase64] = useState(null);
  const [showCam, setShowCam] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showMedHistory, setShowMedHistory] = useState(false);
  const [showRateReview, setShowRateReview] = useState(false);
  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const [showActualCheckInModal, setShowActualCheckInModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [stateList, setStateList] = useState([]);
  const [fieldWriteConfig, setFieldWriteConfig] = useState({});
  const [procedureList, setProcedureList] = useState([]);

  const { fetchPatientData, refreshPatientData } = usePatientData();

  const tokenData = sessionStorage.getItem("tokenData")
    ? JSON.parse(sessionStorage.getItem("tokenData"))
    : null;

  const hasRateReview = !!getRateReviewTemplate();

  const {
    register, reset, handleSubmit, watch, setValue, control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      ...Object.fromEntries(PATIENT_FIELDS.map(k => [k, ""])),
      allowReviewRequest: true,
      allowTestimonialReuse: false,
      communicationPreference: "both",
    },
  });

  const checkIsPending = () => {
    const values = watch();
    const firstName = (values.patientFirstName || "").trim();
    const lastName = (values.patientLastName || "").trim();
    return !firstName || !lastName;
  };

  useImperativeHandle(ref, () => ({
    submit: () => handleSubmit(onSaveAndContinueClick)(),
    isPending: () => checkIsPending(),
  }));

  useEffect(() => {
    register("caseTypeId", { required: "Case type is required" });
  }, [register]);

  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      await Promise.all([
        handleGetStateList(),
        handleGetCaseTypeList(),
        handleGetPatientDetailsFromContext(),
        callGetProcedureCodeListApi()
      ]);
      setLoading(false);
    };
    loadInitialData();
  }, []);

  const handleGetStateList = async () => {
    try {
      const result = await GetStateList(tokenData?.companyId);
      if (result.success) {
        setStateList(result.data || []);
        setSessionItem(StateList, result.data || []);
      }
    } catch (err) { console.error("Error fetching state list:", err); }
  };

  const handleGetCaseTypeList = async () => {
    try {
      const result = await GetCaseTypeList(tokenData?.companyId);
      if (result.success) setSessionItem(CaseTypeList, result.data || []);
    } catch (err) { console.error("Error fetching case type list:", err); }
  };

  const handleGetPatientDetailsFromContext = async () => {
    try {
      const caseId = tokenData?.caseId || tokenData?.CaseId;
      const companyId = tokenData?.companyId || tokenData?.CompanyId;
      if (!caseId || !companyId) {
        toast.error("Something went wrong while fetching your details.");
        return;
      }

      const details = await fetchPatientData(caseId, companyId);
      if (!details) { toast.error("Something went wrong while fetching your details."); return; }

      setSessionItem(PatientDetails, details);

      const formValues = Object.fromEntries(
        PATIENT_FIELDS.map(key => [
          key,
          DATE_FIELDS.includes(key)
            ? ConvertDateFormatForUI(details[key])
            : (details[key] ?? ""),
        ]),
      );
      reset({
        ...formValues,
        allowReviewRequest: details.allowReviewRequest ?? true,
        allowTestimonialReuse: details.allowTestimonialReuse ?? false,
        communicationPreference: details.communicationPreference ?? "both",
      });
      setFieldWriteConfig(details.parsedConfig || {});
      setPhotoData(toImageSrc(details.profileImageData));
      setNewPhotoBase64(null);
      props?.onReady?.("");
    } catch (err) {
      console.error("Error fetching patient details:", err);
      toast.error("Something went wrong while fetching your details.");
    }
  };

  const callGetProcedureCodeListApi = async () => {
    try {
      const response = await GetProcedureCodeList(tokenData?.companyId);
      if (response?.success) {
        setProcedureList(response.data || []);
        sessionStorage.setItem("procedureGroupList", JSON.stringify(response.data || []));
      }
    } catch (err) { console.error("Error fetching procedure code list:", err); }
  };

  const handleUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const err = validatePhoto(file);
    if (err) { toast.error(err); e.target.value = null; return; }
    const reader = new FileReader();
    reader.onloadend = () => { setPhotoData(reader.result); setNewPhotoBase64(reader.result); };
    reader.readAsDataURL(file);
    e.target.value = null;
  };

  const handleCameraCapture = (img) => { setPhotoData(img); setNewPhotoBase64(img); setShowCam(false); };

  const onSaveAndContinueClick = async (data) => {
    try {
      setLoading(true);
      data.caseId = tokenData?.caseId;
      data.companyId = tokenData?.companyId;

      let profileImagePayload = {};
      if (newPhotoBase64) profileImagePayload = { profileImageData: newPhotoBase64 };
      else if (photoData === null) profileImagePayload = { profileImageData: null };

      const payload = {
        ...data,
        ...Object.fromEntries(DATE_FIELDS.map(key => [key, ConvertDateFormatForAPI(data[key])])),
        ...profileImagePayload,
      };

      const response = await UpdatePatientDetails(payload);
      if (!response?.data?.success) {
        toast.error("Failed to save your details.");
        return false;
      }

      toast.success("Your details saved successfully.");

      await refreshPatientData(tokenData?.caseId, tokenData?.companyId);

      props?.onReady?.("");
      props.onApiSuccess?.();
      return true;
    } catch (err) {
      console.error("Error saving patient details:", err);
      toast.error("Failed to save your details. Please try again.");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const formatPhone = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 10);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };
  const isFieldWritable = (configKey) => {
    if (!fieldWriteConfig || !Object.keys(fieldWriteConfig).length) return true;
    return fieldWriteConfig[configKey]?.isWrite !== false;
  };
  const handleHeightInput = (e) => {
    let value = e.target.value.replace(/[^0-9.]/g, "");
    const parts = value.split(".");
    if (parts.length > 2) value = parts[0] + "." + parts.slice(1).join("");
    if (parts[0]) parts[0] = parts[0].slice(0, 2);
    if (parts[1]) parts[1] = parts[1].slice(0, 2);
    value = parts.join(".");
    if (parseFloat(value) > 10) value = "10.00";
    e.target.value = value;
  };
  const handleWeightInput = (e) => {
    let value = e.target.value.replace(/[^0-9.]/g, "");
    const parts = value.split(".");
    if (parts.length > 2) value = parts[0] + "." + parts.slice(1).join("");
    if (parts[0]) parts[0] = parts[0].slice(0, 3);
    if (parts[1]) parts[1] = parts[1].slice(0, 2);
    value = parts.join(".");
    if (parseFloat(value) > 500) value = "500.00";
    e.target.value = value;
  };

  return (
    <>
      {showMedHistory && <MedicalHistory onClose={() => setShowMedHistory(false)} />}
      {showRateReview && <RateReviewModal onClose={() => setShowRateReview(false)} />}

      {loading ? (
        <PatientDetailShimmer />
      ) : (
        <form className="pd-root" onSubmit={handleSubmit(onSaveAndContinueClick)}>
          {showPreview && (
            <ImagePreviewModal
              src={photoData}
              anchorRef={avatarRef}
              onClose={() => setShowPreview(false)}
              onRemove={() => {
                setPhotoData(null);
                setNewPhotoBase64(null);
                setValue("profileImageData", null, { shouldDirty: true });
                setShowPreview(false);
              }}
            />
          )}

          <input ref={fileRef} type="file" accept="image/jpeg,image/jpg,image/png" onChange={handleUpload} style={{ display: "none" }} />

          {/* ── Avatar row ── */}
          <div className="pd-avatar-row">
            <div className="pd-avatar-wrap-row" style={{ flex: 1, minWidth: 0 }}>
              <div className="pd-avatar-wrap" ref={avatarRef}>
                {photoData ? (
                  <img
                    src={photoData}
                    alt="Patient"
                    className="pd-avatar-img"
                    style={{ cursor: "pointer" }}
                    onClick={() => setShowPreview(true)}
                    title="Click to preview"
                  />
                ) : (
                  <div
                    className="pd-avatar-placeholder"
                    style={{ cursor: "pointer" }}
                    onClick={() => setShowCam(true)}
                    title="Add profile picture"
                  >
                    <svg
                      className="pd-avatar-placeholder-icon"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                  </div>
                )}
                <div
                  className="pd-avatar-edit"
                  onClick={() => setShowCam((v) => !v)}
                  data-tooltip={photoData ? "Change profile picture" : "Add profile picture"}
                >
                  <CameraIcon size={11} color="white" />
                </div>
              </div>

              <div className="pd-avatar-info">
                <div className="pd-avatar-actions">
                  {/* Buttons removed from here; we use the main pd-avatar-row-med buttons for both desktop and mobile now */}
                </div>
              </div>
            </div>

            <div className="pd-avatar-row-separator" style={{ width: 1, alignSelf: "stretch", background: "#e5ddd3", flexShrink: 0 }} />

            <div className="pd-avatar-row-med" style={{ display: "flex", flexDirection: "row", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <AppointmentDropdown
                onBookAppointment={() => setShowCheckInModal(true)}
                onCheckInClick={() => setShowActualCheckInModal(true)}
              />
              <MedHistoryBtn onClick={() => setShowMedHistory(true)} />
              {hasRateReview && <RateReviewBtn onClick={() => setShowRateReview(true)} />}
            </div>
          </div>

          <AddAppointmentModal
            isOpen={showCheckInModal}
            onClose={() => setShowCheckInModal(false)}
            patientName={((watch("patientFirstName") || "") + " " + (watch("patientLastName") || "")).trim()}
            onCheckIn={(sig) => {
              console.log("Appointment added with signature", !!sig);
            }}
          />

          <CheckInModal
            isOpen={showActualCheckInModal}
            onClose={() => setShowActualCheckInModal(false)}
            patientName={((watch("patientFirstName") || "") + " " + (watch("patientLastName") || "")).trim()}
            onCheckIn={(sig) => {
              console.log("Check-in completed with signature", !!sig);
            }}
          />

          {showCam && (
            <WebcamModal
              docLabel=""
              onCapture={handleCameraCapture}
              onClose={() => setShowCam(false)}
              onUploadClick={() => {
                setShowCam(false);
                fileRef.current.click();
              }}
            />
          )}

          <div className="pd-grid-4">
            <PFld label={<>First Name <RequiredStar /></>}>
              <input className={`pd-input${errors.patientFirstName ? " pd-input-error" : ""}`} placeholder="First name"
                disabled={!isFieldWritable("patientFirstName")}
                {...register("patientFirstName", { required: "First name is required" })} />
              {errors.patientFirstName && <span className="pd-error-msg">{errors.patientFirstName.message}</span>}
            </PFld>
            <PFld label="Middle Name">
              <input className="pd-input" placeholder="Middle name" disabled={!isFieldWritable("patientMiddleName")} {...register("patientMiddleName")} />
            </PFld>
            <PFld label={<>Last Name <RequiredStar /></>}>
              <input className={`pd-input${errors.patientLastName ? " pd-input-error" : ""}`} placeholder="Last name"
                disabled={!isFieldWritable("patientLastName")}
                {...register("patientLastName", { required: "Last name is required" })} />
              {errors.patientLastName && <span className="pd-error-msg">{errors.patientLastName.message}</span>}
            </PFld>
            <PFld label="Date of Birth">
              <DatePicker value={watch("patientDOB")} onChange={val => setValue("patientDOB", val, { shouldDirty: true })} disabled={!isFieldWritable("patientDOB")} />
            </PFld>
          </div>

          <div className="pd-grid-4">
            <SelectDropdown label="Marital Status"
              options={[{ value: "1", label: "Single" }, { value: "2", label: "Married" }, { value: "3", label: "Other" }]}
              value={watch("patientMaritalStatus")} onChange={val => setValue("patientMaritalStatus", val, { shouldDirty: true })}
              disabled={!isFieldWritable("patientMaritalStatus")} error={errors.patientMaritalStatus?.message} />
            <SelectDropdown label="Gender"
              options={[{ value: "Male", label: "Male" }, { value: "Female", label: "Female" }]}
              value={watch("patientGender")} onChange={val => setValue("patientGender", val, { shouldDirty: true })}
              disabled={!isFieldWritable("patientGender")} error={errors.patientGender?.message} />
            <PFld label="Height (ft)">
              <input className="pd-input" placeholder="e.g. 5.9" disabled={!isFieldWritable("height")} onInput={handleHeightInput}
                {...register("height", { setValueAs: v => v === "" ? "0.00" : v })} />
            </PFld>
            <PFld label="Weight (lbs)">
              <input className="pd-input" placeholder="e.g. 150" disabled={!isFieldWritable("weight")} onInput={handleWeightInput}
                {...register("weight", { setValueAs: v => v === "" ? "0.00" : v })} />
            </PFld>
          </div>

          <div className="pd-grid-4">
            <PFld label="Address">
              <input className="pd-input" placeholder="Street address" disabled={!isFieldWritable("patientAddress")} {...register("patientAddress")} />
            </PFld>
            <PFld label="Street">
              <input className="pd-input" placeholder="Street" disabled={!isFieldWritable("patientStreet")} {...register("patientStreet")} />
            </PFld>
            <PFld label="City">
              <input className="pd-input" placeholder="City" disabled={!isFieldWritable("patientCity")} {...register("patientCity")} />
            </PFld>
            <SelectDropdown label="State"
              options={stateList.map(type => ({ value: type.stateId, label: type.stateName }))}
              value={watch("patientStateId")} onChange={val => setValue("patientStateId", val, { shouldDirty: true })}
              disabled={!isFieldWritable("patientStateId")} error={errors.patientStateId?.message} />
          </div>

          <div className="pd-grid-4">
            <PFld label="Zip Code">
              <input className="pd-input" placeholder="10001" disabled={!isFieldWritable("patientZIP")} {...register("patientZIP")} />
            </PFld>
            <PFld label="Phone">
              <input className="pd-input" placeholder="(555) 000-0000"
                {...register("patientCellNumber")}
                onChange={e => setValue("patientCellNumber", formatPhone(e.target.value), { shouldDirty: true })}
                disabled={!isFieldWritable("patientCellNumber")} />
            </PFld>
            <PFld label="Email Address">
              <input className="pd-input" placeholder="patient@email.com" disabled={!isFieldWritable("patientEmail")} {...register("patientEmail")} />
            </PFld>
            <PFld label="Driver's License #">
              <input className="pd-input" placeholder="License number" disabled={!isFieldWritable("patientDrivingLicense")} {...register("patientDrivingLicense")} />
            </PFld>
          </div>

          {/* ── Review Settings Section ── */}
          {/* <div style={{ marginTop: "40px", paddingTop: "28px", borderTop: "1px solid #e5ddd3" }}> */}

          {/* Allow Review Request */}
          {/* <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "22px" }}>
              <div>
                <div style={{ fontSize: "15px", fontWeight: 500, color: "#333" }}>Allow review requests</div>
                <div style={{ fontSize: "13px", color: "#666", marginTop: "3px" }}>
                  We’ll send you a link to leave a review after your visit.
                </div>
              </div>
              <label style={{ position: "relative", display: "inline-block", width: "46px", height: "24px" }}>
                <input
                  type="checkbox"
                  {...register("allowReviewRequest")}
                  style={{ opacity: 0, width: 0, height: 0 }}
                />
                <span style={{
                  position: "absolute", cursor: "pointer", top: 0, left: 0, right: 0, bottom: 0,
                  backgroundColor: watch("allowReviewRequest") ? "#0aa375" : "#ccc",
                  borderRadius: "34px", transition: "0.3s"
                }}>
                  <span style={{
                    position: "absolute",
                    height: "18px",
                    width: "18px",
                    left: watch("allowReviewRequest") ? "24px" : "3px",
                    bottom: "3px",
                    backgroundColor: "white",
                    borderRadius: "50%",
                    transition: "0.3s"
                  }}></span>
                </span>
              </label>
            </div> */}

          {/* Send the review link by
            <div style={{ marginBottom: "22px" }}>
              <div style={{ fontSize: "15px", fontWeight: 500, color: "#333", marginBottom: "10px" }}>
                Send the review link by
              </div>
              <div style={{ display: "flex", gap: "12px" }}>
                {[
                  { value: "sms", label: "SMS", icon: "💬" },
                  { value: "email", label: "Email", icon: "📧" },
                  { value: "both", label: "Both", icon: "✓✓" }
                ].map((item) => (
                  <label key={item.value} style={{ cursor: "pointer" }}>
                    <input
                      type="radio"
                      value={item.value}
                      {...register("communicationPreference")}
                      style={{ display: "none" }}
                    />
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "10px 22px",
                      borderRadius: "8px",
                      fontSize: "14.5px",
                      fontWeight: 500,
                      backgroundColor: watch("communicationPreference") === item.value ? "#0aa375" : "#f0f0f0",
                      color: watch("communicationPreference") === item.value ? "#fff" : "#333",
                      border: "1px solid #ddd",
                      transition: "0.2s"
                    }}>
                      <span style={{ fontSize: "18px" }}>{item.icon}</span>
                      {item.label}
                    </div>
                  </label>
                ))}
              </div>
            </div> */}

          {/* Allow Testimonial Reuse */}
          {/* <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: "15px", fontWeight: 500, color: "#333" }}>Allow testimonial reuse</div>
                <div style={{ fontSize: "13px", color: "#666", marginTop: "3px" }}>
                  Let us feature your review as a testimonial on our site.
                </div>
              </div>
              <label style={{ position: "relative", display: "inline-block", width: "46px", height: "24px" }}>
                <input
                  type="checkbox"
                  {...register("allowTestimonialReuse")}
                  style={{ opacity: 0, width: 0, height: 0 }}
                />
                <span style={{
                  position: "absolute", cursor: "pointer", top: 0, left: 0, right: 0, bottom: 0,
                  backgroundColor: watch("allowTestimonialReuse") ? "#0aa375" : "#ccc",
                  borderRadius: "34px", transition: "0.3s"
                }}>
                  <span style={{
                    position: "absolute",
                    height: "18px",
                    width: "18px",
                    left: watch("allowTestimonialReuse") ? "24px" : "3px",
                    bottom: "3px",
                    backgroundColor: "white",
                    borderRadius: "50%",
                    transition: "0.3s"
                  }}></span>
                </span>
              </label>
            </div> */}
          {/* </div> */}
        </form>
      )}
    </>
  );
});

export default PatientDetail;